<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title>Teams</v-toolbar-title>
      <v-spacer></v-spacer>
      <div class="mr-5 pt-5">
        <v-switch class="" label="Show All" v-model="showAll"></v-switch>
      </div>
      <v-btn
        outlined
        color="primary"
        class="mb-2 mr-2"
        @click.native.stop="openDefaultsDialog"
        >Target Defaults</v-btn
      >
      <v-btn
        @click.native.stop="dialog = true"
        color="primary"
        dark
        class="mb-2"
        >New Team</v-btn
      >
    </v-toolbar>
    <v-row>
      <v-col cols="12">
        <!-- Team create/edit dialog -->
        <v-dialog v-model="dialog" max-width="600px">
          <v-card>
            <v-card-title>
              <span class="headline">{{ formTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="editedItem.name"
                      label="Team Name"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-switch
                      v-model="editedItem.active"
                      label="Active"
                    ></v-switch>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="editedItem.divisionId"
                      label="Division"
                      :items="divisionOptions"
                      clearable
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete
                      v-model="editedItem.userIds"
                      label="Members"
                      :items="userOptions"
                      chips
                      multiple
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" v-if="editedItem.userIds.length">
                    <div class="subtitle-2 mb-2">Member Roles</div>
                    <v-row
                      v-for="userId in editedItem.userIds"
                      :key="userId"
                      align="center"
                      dense
                    >
                      <v-col cols="7">{{ userName(userId) }}</v-col>
                      <v-col cols="5">
                        <v-select
                          dense
                          hide-details
                          :items="roleOptions"
                          v-model="editedItem.roles[userId]"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click.native="close"
                >Cancel</v-btn
              >
              <v-btn color="blue darken-1" text @click.native="save"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Targets dialog -->
        <v-dialog v-model="targetsDialog" max-width="700px">
          <v-card v-if="targetsTeam">
            <v-card-title>
              <span class="headline">{{ targetsTeam.name }} — Targets</span>
            </v-card-title>
            <v-card-text>
              <p class="caption mb-4">
                An override takes effect in its month and carries forward until
                a later override replaces it or it is removed — you don't need
                to re-create it each month. Leave a field blank to use the
                computed default (the sum of each member's per-role target).
                Per-paralegal defaults:
                {{ defaults.perParalegalDemands }} demands,
                {{ defaults.perParalegalSettlements }} settlements,
                {{ formatCurrency(defaults.perParalegalFees) }} fees, and
                {{ defaults.perParalegalReviews }} reviews. Score goals default
                to {{ defaults.fileReviewGoal }}% file review and
                {{ defaults.clientContactGoal }}% client contact.
              </p>
              <v-row dense>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="targetForm.month"
                    label="Effective from"
                    type="month"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field
                    v-model.number="targetForm.demandsTarget"
                    label="Demands"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model.number="targetForm.settlementsTarget"
                    label="Settlements"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field
                    v-model.number="targetForm.feesTarget"
                    label="Fees"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="2">
                  <v-text-field
                    v-model.number="targetForm.reviewsTarget"
                    label="Reviews"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="targetForm.fileReviewTarget"
                    label="File Review Goal"
                    type="number"
                    suffix="%"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="targetForm.clientContactTarget"
                    label="Client Contact Goal"
                    type="number"
                    suffix="%"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="targetForm.note"
                    label="Note (optional)"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="text-right">
                <v-btn color="primary" small @click="saveTarget"
                  >Save Override</v-btn
                >
              </div>

              <v-divider class="my-4"></v-divider>
              <div class="subtitle-2 mb-2">Existing Overrides</div>
              <v-data-table
                :headers="targetHeaders"
                :items="targetsTeam.targets || []"
                hide-default-footer
                dense
              >
                <template v-slot:item.month="{ item }">
                  {{ formatMonth(item.month) }}
                </template>
                <template v-slot:item.feesTarget="{ item }">
                  {{ item.feesTarget ? formatCurrency(item.feesTarget) : "—" }}
                </template>
                <template v-slot:item.reviewsTarget="{ item }">
                  {{
                    item.reviewsTarget !== null &&
                    item.reviewsTarget !== undefined
                      ? item.reviewsTarget
                      : "—"
                  }}
                </template>
                <template v-slot:item.fileReviewTarget="{ item }">
                  {{
                    item.fileReviewTarget !== null &&
                    item.fileReviewTarget !== undefined
                      ? item.fileReviewTarget + "%"
                      : "—"
                  }}
                </template>
                <template v-slot:item.clientContactTarget="{ item }">
                  {{
                    item.clientContactTarget !== null &&
                    item.clientContactTarget !== undefined
                      ? item.clientContactTarget + "%"
                      : "—"
                  }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon small color="pink" @click="deleteTarget(item)"
                    >mdi-delete</v-icon
                  >
                </template>
              </v-data-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click.native="targetsDialog = false"
                >Close</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Target defaults dialog -->
        <v-dialog v-model="defaultsDialog" max-width="640px">
          <v-card>
            <v-card-title>
              <span class="headline">Per-Role Target Defaults</span>
            </v-card-title>
            <v-card-text>
              <p class="caption grey--text mb-1">
                Monthly targets per team member of each role. Leave a role's
                fields at 0 for no target. A team's target is the sum across its
                members.
              </p>
              <div class="subtitle-2 mt-2">Paralegal</div>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perParalegalDemands"
                    label="Demands"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perParalegalSettlements"
                    label="Settlements"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perParalegalFees"
                    label="Fees"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="subtitle-2 mt-2">Negotiator</div>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perNegotiatorDemands"
                    label="Demands"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perNegotiatorSettlements"
                    label="Settlements"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perNegotiatorFees"
                    label="Fees"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="subtitle-2 mt-2">Legal Assistant</div>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perLegalAssistantDemands"
                    label="Demands"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perLegalAssistantSettlements"
                    label="Settlements"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perLegalAssistantFees"
                    label="Fees"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="subtitle-2 mt-2">Reviews (per month)</div>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perParalegalReviews"
                    label="Paralegal"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perNegotiatorReviews"
                    label="Negotiator"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model.number="defaultsForm.perLegalAssistantReviews"
                    label="Legal Assistant"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="subtitle-2 mt-2">Quality Score Goals (%)</div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="defaultsForm.fileReviewGoal"
                    label="File Review Score"
                    type="number"
                    suffix="%"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="defaultsForm.clientContactGoal"
                    label="Client Contact Score"
                    type="number"
                    suffix="%"
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="subtitle-2 mt-2">
                Deadline Goals (max per team, lower is better)
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="defaultsForm.overdueDeadlinesGoal"
                    label="Overdue Deadlines"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="defaultsForm.upcomingDeadlinesGoal"
                    label="Upcoming Deadlines (next 90 days)"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click.native="defaultsDialog = false"
                >Cancel</v-btn
              >
              <v-btn color="blue darken-1" text @click.native="saveDefaults"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-data-table
          :footer-props="{
            'items-per-page-options': [-1]
          }"
          :headers="headers"
          hide-default-footer
          :items="teams"
          :loading="$apollo.loading"
          :sort-by="['name']"
          :sort-desc="[false]"
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>{{ item.name }}</td>
                <td>{{ item.divisionName }}</td>
                <td>{{ item.attorneyNames }}</td>
                <td>{{ item.paralegalNames }}</td>
                <td>
                  <v-simple-checkbox
                    color="primary"
                    :value="item.active"
                    :ripple="false"
                  />
                </td>
                <td class="justify-center layout px-0">
                  <v-icon
                    small
                    class="mr-2"
                    color="indigo"
                    @click="openTargets(item)"
                    >mdi-target</v-icon
                  >
                  <v-icon
                    small
                    class="mr-2"
                    color="teal"
                    @click="editItem(item)"
                    >mdi-account-edit</v-icon
                  >
                  <v-icon small color="pink" @click="deleteItem(item)"
                    >mdi-delete</v-icon
                  >
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import filter from "lodash/filter";

export default {
  data() {
    return {
      dialog: false,
      targetsDialog: false,
      defaultsDialog: false,
      headers: [
        { text: "Name", value: "name" },
        { text: "Division", value: "divisionName" },
        { text: "Attorney", value: "attorneyNames" },
        { text: "Paralegals", value: "paralegalNames" },
        { text: "Active", value: "active" },
        { text: "Actions", align: "center", value: "name", sortable: false }
      ],
      roleOptions: [
        { text: "Attorney", value: "attorney" },
        { text: "Paralegal", value: "paralegal" },
        { text: "Negotiator", value: "negotiator" },
        { text: "Legal Assistant", value: "legal_assistant" }
      ],
      editedIndex: -1,
      editedItem: {
        name: "",
        active: true,
        divisionId: null,
        userIds: [],
        roles: {}
      },
      defaultItem: {
        name: "",
        active: true,
        divisionId: null,
        userIds: [],
        roles: {}
      },
      userOptions: [],
      divisionOptions: [],
      showAll: false,
      // Targets
      targetsTeam: null,
      targetForm: this.emptyTargetForm(),
      targetHeaders: [
        { text: "Effective From", value: "month" },
        { text: "Demands", value: "demandsTarget" },
        { text: "Settlements", value: "settlementsTarget" },
        { text: "Fees", value: "feesTarget" },
        { text: "Reviews", value: "reviewsTarget" },
        { text: "File Review", value: "fileReviewTarget" },
        { text: "Client Contact", value: "clientContactTarget" },
        { text: "", value: "actions", sortable: false, align: "right" }
      ],
      // Defaults
      defaults: {
        perParalegalDemands: 6,
        perParalegalSettlements: 5,
        perParalegalFees: 60000,
        perNegotiatorDemands: 0,
        perNegotiatorSettlements: 0,
        perNegotiatorFees: 0,
        perLegalAssistantDemands: 0,
        perLegalAssistantSettlements: 0,
        perLegalAssistantFees: 0,
        perParalegalReviews: 2,
        perNegotiatorReviews: 0,
        perLegalAssistantReviews: 0,
        fileReviewGoal: 90,
        clientContactGoal: 90,
        overdueDeadlinesGoal: 0,
        upcomingDeadlinesGoal: 10
      },
      defaultsForm: {
        perParalegalDemands: 6,
        perParalegalSettlements: 5,
        perParalegalFees: 60000,
        perNegotiatorDemands: 0,
        perNegotiatorSettlements: 0,
        perNegotiatorFees: 0,
        perLegalAssistantDemands: 0,
        perLegalAssistantSettlements: 0,
        perLegalAssistantFees: 0,
        perParalegalReviews: 2,
        perNegotiatorReviews: 0,
        perLegalAssistantReviews: 0,
        fileReviewGoal: 90,
        clientContactGoal: 90,
        overdueDeadlinesGoal: 0,
        upcomingDeadlinesGoal: 10
      }
    };
  },
  computed: {
    teams() {
      let teams = get(this.organization, "teams");
      if (teams && teams.length > 0) {
        let mappedTeams = teams.map(t => {
          const members = t.members || [];
          const attorneys = members.filter(m => m.role === "attorney");
          const paralegals = members.filter(m => m.role !== "attorney");
          const roles = {};
          members.forEach(m => {
            roles[m.user.id] = m.role || "paralegal";
          });
          return {
            id: t.id,
            name: t.name,
            active: t.active,
            divisionId: t.division ? t.division.id : null,
            divisionName: t.division ? t.division.name : "—",
            attorneyNames:
              attorneys.map(m => fullName(m.user)).join(", ") || "—",
            paralegalNames:
              paralegals.map(m => fullName(m.user)).join(", ") || "—",
            userIds: members.map(m => m.user.id),
            roles,
            targets: t.targets || []
          };
        });
        if (this.showAll) {
          return mappedTeams;
        } else {
          return filter(mappedTeams, t => t.active);
        }
      } else {
        return [];
      }
    },
    formTitle() {
      return this.editedIndex === -1 ? "New Team" : "Edit Team";
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    "organization.users"(val) {
      this.userOptions = sortBy(
        val.map(u => {
          return { text: `${u.firstName} ${u.lastName}`, value: u.id };
        }),
        [item => item.text]
      );
    },
    "organization.divisions"(val) {
      this.divisionOptions = sortBy(
        (val || []).map(d => ({ text: d.name, value: d.id })),
        [item => item.text]
      );
    },
    "editedItem.userIds"(ids) {
      // Ensure each selected member has a role (default paralegal).
      ids.forEach(id => {
        if (!this.editedItem.roles[id]) {
          this.$set(this.editedItem.roles, id, "paralegal");
        }
      });
    }
  },
  apollo: {
    organization: {
      query: gql`
        query($id: ID!) {
          organization(id: $id) {
            id
            name
            users {
              id
              firstName
              lastName
            }
            divisions {
              id
              name
            }
            teams {
              id
              name
              active
              division {
                id
                name
              }
              members {
                role
                user {
                  id
                  firstName
                  lastName
                }
              }
              targets {
                id
                month
                demandsTarget
                settlementsTarget
                feesTarget
                reviewsTarget
                fileReviewTarget
                clientContactTarget
                note
              }
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id
        };
      }
    },
    targetDefaults: {
      query: gql`
        query {
          targetDefaults {
            id
            perParalegalDemands
            perParalegalSettlements
            perParalegalFees
            perNegotiatorDemands
            perNegotiatorSettlements
            perNegotiatorFees
            perLegalAssistantDemands
            perLegalAssistantSettlements
            perLegalAssistantFees
            perParalegalReviews
            perNegotiatorReviews
            perLegalAssistantReviews
            fileReviewGoal
            clientContactGoal
            overdueDeadlinesGoal
            upcomingDeadlinesGoal
          }
        }
      `,
      result({ data }) {
        if (data && data.targetDefaults) {
          this.defaults = { ...data.targetDefaults };
        }
      }
    }
  },
  methods: {
    emptyTargetForm() {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      return {
        month,
        demandsTarget: null,
        settlementsTarget: null,
        feesTarget: null,
        reviewsTarget: null,
        fileReviewTarget: null,
        clientContactTarget: null,
        note: ""
      };
    },
    userName(userId) {
      const opt = this.userOptions.find(o => o.value === userId);
      return opt ? opt.text : userId;
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return "";
      const num = typeof value === "string" ? parseFloat(value) : value;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    },
    formatMonth(monthStr) {
      if (!monthStr) return "";
      const [year, month] = monthStr.split("-").map(Number);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ];
      return `${monthNames[month - 1]} ${year}`;
    },
    editItem(item) {
      this.editedIndex = this.teams.indexOf(item);
      this.editedItem = {
        id: item.id,
        name: item.name,
        active: item.active,
        divisionId: item.divisionId,
        userIds: [...item.userIds],
        roles: Object.assign({}, item.roles)
      };
      this.dialog = true;
    },
    deleteItem() {
      confirm("Are you sure you want to delete this team?");
    },
    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem, {
          userIds: [],
          roles: {}
        });
        this.editedIndex = -1;
      });
    },
    save() {
      const members = this.editedItem.userIds.map(id => ({
        userId: id,
        role: this.editedItem.roles[id] || "paralegal"
      }));
      const team = {
        name: this.editedItem.name,
        active: this.editedItem.active,
        divisionId: this.editedItem.divisionId,
        members
      };
      if (this.editedItem.id) {
        team.id = this.editedItem.id;
      }
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($team: UpsertTeamInput) {
              upsertTeam(team: $team) {
                id
                name
                active
              }
            }
          `,
          variables: {
            team
          }
        })
        .then(() => {
          this.close();
          this.$apollo.queries.organization.refetch();
        })
        .catch(error => {
          console.error(error);
        });
    },
    openTargets(item) {
      this.targetsTeam = item;
      this.targetForm = this.emptyTargetForm();
      this.targetsDialog = true;
    },
    saveTarget() {
      const target = {
        teamId: parseInt(this.targetsTeam.id, 10),
        month: `${this.targetForm.month}-01`,
        demandsTarget: numberOrNull(this.targetForm.demandsTarget),
        settlementsTarget: numberOrNull(this.targetForm.settlementsTarget),
        feesTarget: numberOrNull(this.targetForm.feesTarget),
        reviewsTarget: numberOrNull(this.targetForm.reviewsTarget),
        fileReviewTarget: numberOrNull(this.targetForm.fileReviewTarget),
        clientContactTarget: numberOrNull(this.targetForm.clientContactTarget),
        note: this.targetForm.note || null
      };
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($target: UpsertTeamTargetInput!) {
              upsertTeamTarget(target: $target) {
                id
              }
            }
          `,
          variables: { target }
        })
        .then(() => {
          this.targetForm = this.emptyTargetForm();
          return this.$apollo.queries.organization.refetch();
        })
        .then(() => {
          // Re-bind the targets dialog to the refreshed team data.
          const refreshed = this.teams.find(t => t.id === this.targetsTeam.id);
          if (refreshed) this.targetsTeam = refreshed;
        })
        .catch(error => console.error(error));
    },
    deleteTarget(item) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($id: ID!) {
              deleteTeamTarget(id: $id) {
                id
              }
            }
          `,
          variables: { id: item.id }
        })
        .then(() => this.$apollo.queries.organization.refetch())
        .then(() => {
          const refreshed = this.teams.find(t => t.id === this.targetsTeam.id);
          if (refreshed) this.targetsTeam = refreshed;
        })
        .catch(error => console.error(error));
    },
    openDefaultsDialog() {
      this.defaultsForm = Object.assign({}, this.defaults);
      this.defaultsDialog = true;
    },
    saveDefaults() {
      const f = this.defaultsForm;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation($defaults: UpsertTargetDefaultsInput!) {
              upsertTargetDefaults(defaults: $defaults) {
                id
                perParalegalDemands
                perParalegalSettlements
                perParalegalFees
                perNegotiatorDemands
                perNegotiatorSettlements
                perNegotiatorFees
                perLegalAssistantDemands
                perLegalAssistantSettlements
                perLegalAssistantFees
                perParalegalReviews
                perNegotiatorReviews
                perLegalAssistantReviews
                fileReviewGoal
                clientContactGoal
                overdueDeadlinesGoal
                upcomingDeadlinesGoal
              }
            }
          `,
          variables: {
            defaults: {
              perParalegalDemands: f.perParalegalDemands,
              perParalegalSettlements: f.perParalegalSettlements,
              perParalegalFees: f.perParalegalFees,
              perNegotiatorDemands: f.perNegotiatorDemands || 0,
              perNegotiatorSettlements: f.perNegotiatorSettlements || 0,
              perNegotiatorFees: f.perNegotiatorFees || 0,
              perLegalAssistantDemands: f.perLegalAssistantDemands || 0,
              perLegalAssistantSettlements: f.perLegalAssistantSettlements || 0,
              perLegalAssistantFees: f.perLegalAssistantFees || 0,
              perParalegalReviews:
                f.perParalegalReviews != null ? f.perParalegalReviews : 2,
              perNegotiatorReviews: f.perNegotiatorReviews || 0,
              perLegalAssistantReviews: f.perLegalAssistantReviews || 0,
              fileReviewGoal: f.fileReviewGoal != null ? f.fileReviewGoal : 90,
              clientContactGoal:
                f.clientContactGoal != null ? f.clientContactGoal : 90,
              overdueDeadlinesGoal:
                f.overdueDeadlinesGoal != null ? f.overdueDeadlinesGoal : 0,
              upcomingDeadlinesGoal:
                f.upcomingDeadlinesGoal != null ? f.upcomingDeadlinesGoal : 10
            }
          }
        })
        .then(({ data }) => {
          if (data && data.upsertTargetDefaults) {
            this.defaults = { ...data.upsertTargetDefaults };
          }
          this.defaultsDialog = false;
        })
        .catch(error => console.error(error));
    }
  }
};

function fullName(user) {
  return `${user.firstName} ${user.lastName}`;
}

function numberOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(num) ? null : num;
}
</script>

<style scoped></style>
